import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { map, Observable } from 'rxjs';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { StudentPauseRequestArgument } from './students-pause.argument';
import {
	PauseExitReasonModel,
	StudentPauseRequestModel,
} from './students-pause.model';

@Injectable({
	providedIn: 'root',
})
export class StudentsPauseService {
	constructor(private httpClient: HttpClient) {}

	public requestPause = (
		uid: number,
		args: StudentPauseRequestArgument,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/pause/request`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '6AEEA235',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public createPause = (
		uid: number,
		args: StudentPauseRequestArgument,
	): Observable<StudentPauseRequestModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/pause`, args)
			.pipe(
				mapResponse((r) => plainToInstance(StudentPauseRequestModel, r), {
					errorCode: 'A2B3C4D5',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editPauseRequest = (
		uid: number,
		requestId: number,
		args: StudentPauseRequestArgument,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/pause/request/${requestId}`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '11C143AE',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public getPauseRequest = (
		uid: number,
	): Observable<StudentPauseRequestModel> => {
		return this.httpClient
			.get<StudentPauseRequestModel>(`/api/v1/students/${uid}/pause/request`)
			.pipe(
				map((r) => (Object.keys(r ?? {}).length === 0 ? null : r)),
				mapResponse((r) => plainToInstance(StudentPauseRequestModel, r), {
					errorCode: '8356E4E4',
					errorMessage: DefaultErrorMessage.Loading,
				}),
			);
	};

	public rejectPauseRequest = (
		studentId: number,
		requestId: number,
		reason: string,
	) => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/pause/request/${requestId}/reject`,
				{
					reason: reason,
				},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '3001AC22',
				}),
			);
	};

	public endPause = (uid: number): Observable<StudentPauseRequestModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/pause/end`, {})
			.pipe(
				mapResponse((r) => plainToInstance(StudentPauseRequestModel, r), {
					errorCode: '6C5311B6',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public getPauseOptions = (uid: number): Observable<PauseExitReasonModel> => {
		return this.httpClient
			.get<unknown>(`/api/v1/students/${uid}/pause/options`)
			.pipe(
				mapResponse((r) => plainToInstance(PauseExitReasonModel, r), {
					errorCode: 'B5F3892E',
				}),
			);
	};

	public authorizePauseRequest = (
		studentId: number,
		requestId: number,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/pause/request/${requestId}/authorize`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '24D53B95',
				}),
			);
	};

	public approvePauseRequest = (
		studentId: number,
		requestId: number,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${studentId}/pause/request/${requestId}/approve`,
				{},
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'E92A0287',
				}),
			);
	};
}
